import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaFileExport, FaPlus, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Collapse } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { exportSurveys } from '../../functions/surveyExport';
import { formatDate } from '../../i18n/format';

export const InfoTab = ({ project, drillhole, handleDrillholeDelete, handleDrillholeEdit }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [exportError, setExportError] = useState('');
    const [isCompleted, setIsCompleted] = useState(drillhole.isCompleted);
    const [defaultLocation, setDefaultLocation] = useState({ latitude: 0, longitude: 0 });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editLocation, setEditLocation] = useState({ x: 0, y: 0, z: 0 });
    const [metadataExpanded, setMetadataExpanded] = useState(false);
    const [showMetadataModal, setShowMetadataModal] = useState(false);
    const [editingMetadataIndex, setEditingMetadataIndex] = useState(null);
    const [metadataForm, setMetadataForm] = useState({ key: '', value: '' });

    useEffect(() => {
        setIsCompleted(drillhole.isCompleted);
    }, [drillhole.isCompleted]);

    useEffect(() => {
        fetchDefaultLocation();
    }, []);

    const fetchDefaultLocation = async () => {
        try {
            const response = await fetch('api/identity/customersettings');
            if (response.ok) {
                const data = await response.json();
                setDefaultLocation({
                    latitude: data.defaultLatitude ?? 0,
                    longitude: data.defaultLongitude ?? 0
                });
            }
        } catch (err) {
            console.error('Error fetching default location:', err);
        }
    };

    const getEffectiveLocation = () => {
        const hasValidX = drillhole.location?.x && drillhole.location.x !== 0;
        const hasValidY = drillhole.location?.y && drillhole.location.y !== 0;

        return {
            x: hasValidX ? drillhole.location.x : defaultLocation.latitude,
            y: hasValidY ? drillhole.location.y : defaultLocation.longitude,
            z: drillhole.location?.z ?? 0
        };
    };


    function ChangeMapView({ coords }) {
        const map = useMap();
        map.setView(coords, map.getZoom());

        return null;
    }

    const openAddMetadataModal = () => {
        setEditingMetadataIndex(null);
        setMetadataForm({ key: '', value: '' });
        setShowMetadataModal(true);
    };

    const openEditMetadataModal = (index) => {
        const meta = drillhole.metadata[index];
        setEditingMetadataIndex(index);
        setMetadataForm({ key: meta.key, value: meta.value });
        setShowMetadataModal(true);
    };

    const handleSaveMetadata = async () => {
        if (!metadataForm.key.trim()) {
            alert(t('infoTab.keyRequired'));
            return;
        }

        setLoading(true);
        let updatedMetadata = [...(drillhole.metadata || [])];

        if (editingMetadataIndex !== null) {
            updatedMetadata[editingMetadataIndex] = { key: metadataForm.key, value: metadataForm.value };
        } else {
            updatedMetadata.push({ key: metadataForm.key, value: metadataForm.value });
        }

        try {
            const response = await fetch('api/projects/updatedrillholemetadata', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'project': project.name,
                    'drillhole': drillhole.name
                },
                body: JSON.stringify(updatedMetadata)
            });
            if (response.ok) {
                const updatedMap = await response.json();
                handleDrillholeEdit(updatedMap);
                setShowMetadataModal(false);
            } else {
                alert(t('infoTab.saveMetadataFailed'));
            }
        } catch (err) {
            console.error('Error saving metadata:', err);
            alert(t('infoTab.saveMetadataFailed'));
        }
        setLoading(false);
    };

    const handleDeleteMetadata = async (index) => {
        if (!window.confirm(t('infoTab.confirmDeleteMetadata', { key: drillhole.metadata[index].key }))) {
            return;
        }

        setLoading(true);
        let updatedMetadata = [...drillhole.metadata];
        updatedMetadata.splice(index, 1);

        try {
            const response = await fetch('api/projects/updatedrillholemetadata', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'project': project.name,
                    'drillhole': drillhole.name
                },
                body: JSON.stringify(updatedMetadata)
            });
            if (response.ok) {
                const updatedMap = await response.json();
                handleDrillholeEdit(updatedMap);
            } else {
                alert(t('infoTab.deleteMetadataFailed'));
            }
        } catch (err) {
            console.error('Error deleting metadata:', err);
            alert(t('infoTab.deleteMetadataFailed'));
        }
        setLoading(false);
    };

    // Every survey in the hole as one flat, depth-ordered table - the Merged
    // layout exists to preserve exactly that shape. The CSV is rendered
    // server-side under the customer's export template, which replaced this
    // function's own copy of the mining and grid-correction rules; note the
    // correction is now anchored on each survey's own date rather than the
    // drillhole's, so a hole surveyed long after it was collared gets the
    // correction of the epoch it was measured in.
    async function handleDrillholeExport() {
        setLoading(true);
        setExportError('');
        try {
            await exportSurveys(
                drillhole.surveys.map(reference => reference.fileId),
                'Merged',
                `${drillhole.name}_full.csv`);
        } catch (err) {
            setExportError(err.message);
        }
        setLoading(false);
    }

    const handleIsCompletedToggle = () => {
        setLoading(true);
        fetch('api/projects/markdrillholecomplete', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'project': project.name,
                'drillhole': drillhole.name,
                'isCompleted': !isCompleted
            },
        })
            .then(response => response.json())
            .then(data => {
                setIsCompleted(!isCompleted);
                handleDrillholeEdit(data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error marking drillhole as completed:', error);
                setLoading(false);
            });
    }

    function renderIsCompletedToggle() {
        return (
            <div className="form-check form-switch">
                <input className="form-check-input" type="checkbox" id="isCompletedToggle" checked={isCompleted} onChange={handleIsCompletedToggle} disabled={loading} />
                <label className="form-check-label" htmlFor="isCompletedToggle">{isCompleted ? t('common.yes') : t('common.no')}</label>
            </div>
        );
    }

    const openEditModal = () => {
        setEditLocation({
            x: drillhole.location?.x ?? 0,
            y: drillhole.location?.y ?? 0,
            z: drillhole.location?.z ?? 0
        });
        setShowEditModal(true);
    };

    const handleSaveLocation = async () => {
        setLoading(true);
        try {
            const response = await fetch('api/projects/updatedrillholelocation', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'project': project.name,
                    'drillhole': drillhole.name
                },
                body: JSON.stringify(editLocation)
            });
            if (response.ok) {
                const updatedMap = await response.json();
                handleDrillholeEdit(updatedMap);
                setShowEditModal(false);
            } else {
                alert(t('infoTab.updateLocationFailed'));
            }
        } catch (err) {
            console.error('Error updating location:', err);
            alert(t('infoTab.updateLocationFailed'));
        }
        setLoading(false);
    };

    return (
        <div className="container" style={{ marginTop: 10 }}>
            <div className="row">
                {exportError && (
                    <div className="col-lg-12">
                        <div className="alert alert-danger alert-dismissible" role="alert">
                            {exportError}
                            <button type="button" className="btn-close" onClick={() => setExportError('')}></button>
                        </div>
                    </div>
                )}
                <div className="col-lg-12">
                    <div className="d-flex">
                        <h3>{t('infoTab.title')}</h3>

                        <button className="btn btn-primary ms-auto" disabled={drillhole.surveys.length < 1 || loading} style={{ marginRight: 10 }} onClick={handleDrillholeExport}><FaFileExport /> {t('common.export')}</button>
                        <button className="btn btn-primary" style={{ marginRight: 10 }} onClick={openEditModal}><FaEdit /> {t('common.edit')}</button>
                        <button className="btn btn-primary" style={{ marginRight: 10 }} onClick={() => { handleDrillholeDelete(drillhole) }}><FaTrash /> {t('common.delete')}</button>
                    </div>
                </div>
                <div className="col-lg-12">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>{t('survey.rig')}</th>
                                <th>{t('infoTab.completed')}</th>
                                <th>{t('infoTab.created')}</th>
                                <th>{t('survey.latitude')}</th>
                                <th>{t('survey.longitude')}</th>
                                <th>{t('survey.elevation')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{drillhole.rigId}</td>
                                <td>{renderIsCompletedToggle()}</td>
                                <td>{formatDate(drillhole.created)}</td>
                                <td>{drillhole.location?.x}</td>
                                <td>{drillhole.location?.y}</td>
                                <td>{drillhole.location?.z}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div style={{ marginTop: 20, marginBottom: 20 }}>
                        <h5
                            onClick={() => setMetadataExpanded(!metadataExpanded)}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            {metadataExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            {' '}{t('infoTab.metadataCount', { count: drillhole.metadata?.length || 0 })}
                        </h5>
                        <Collapse isOpen={metadataExpanded}>
                            <table className="table table-sm">
                                <tbody>
                                    {drillhole.metadata?.map((meta, index) => (
                                        <tr key={index}>
                                            <th>{meta.key}</th>
                                            <td>{meta.value}</td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <button className="btn btn-sm btn-secondary"
                                                    style={{ marginRight: 5 }}
                                                    onClick={() => openEditMetadataModal(index)}
                                                    disabled={loading}>
                                                    <FaEdit /></button>
                                                <button className="btn btn-sm btn-danger"
                                                    onClick={() => handleDeleteMetadata(index)}
                                                    disabled={loading}>
                                                    <FaTrash /></button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td colSpan={3}>
                                            <button className="btn btn-sm btn-primary"
                                                onClick={openAddMetadataModal}
                                                disabled={loading}>
                                                <FaPlus /> {t('infoTab.addField')}</button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Collapse>
                    </div>
                    {drillhole.description && <p> {t('drillholes.descriptionLabel')} {drillhole.description || t('common.empty')}</p>}
                    <div style={{ width: '100%', height: 500 }}>
                        <MapContainer center={[getEffectiveLocation().x, getEffectiveLocation().y]} zoom={8}>
                            <TileLayer
                                attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.osm.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={[getEffectiveLocation().x, getEffectiveLocation().y]}>
                                <Popup>
                                    {drillhole.name} <br /> {getEffectiveLocation().x} , {getEffectiveLocation().y} , {getEffectiveLocation().z}
                                </Popup>
                            </Marker>
                            {/*<Polyline positions={[[drillhole.location.x, drillhole.location.y], [drillhole.location.x - 0.003, drillhole.location.y + 0.001]]} />*/}
                            <ChangeMapView coords={[getEffectiveLocation().x, getEffectiveLocation().y]} />
                        </MapContainer>
                    </div>
                </div>
            </div>

            <Modal isOpen={showEditModal} toggle={() => setShowEditModal(false)}>
                <ModalHeader toggle={() => setShowEditModal(false)}>{t('infoTab.editLocation')}</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="editLatitude">{t('survey.latitude')}</Label>
                            <Input
                                type="number"
                                id="editLatitude"
                                step="any"
                                value={editLocation.x}
                                onChange={(e) => setEditLocation({ ...editLocation, x: parseFloat(e.target.value) || 0 })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="editLongitude">{t('survey.longitude')}</Label>
                            <Input
                                type="number"
                                id="editLongitude"
                                step="any"
                                value={editLocation.y}
                                onChange={(e) => setEditLocation({ ...editLocation, y: parseFloat(e.target.value) || 0 })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="editElevation">{t('survey.elevation')}</Label>
                            <Input
                                type="number"
                                id="editElevation"
                                step="any"
                                value={editLocation.z}
                                onChange={(e) => setEditLocation({ ...editLocation, z: parseFloat(e.target.value) || 0 })}
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowEditModal(false)}>{t('common.cancel')}</Button>
                    <Button color="primary" onClick={handleSaveLocation} disabled={loading}>
                        {loading ? t('common.saving') : t('common.save')}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showMetadataModal} toggle={() => setShowMetadataModal(false)}>
                <ModalHeader toggle={() => setShowMetadataModal(false)}>
                    {editingMetadataIndex !== null ? t('infoTab.editMetadata') : t('infoTab.addMetadata')}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="metadataKey">{t('infoTab.key')}</Label>
                            <Input
                                type="text"
                                id="metadataKey"
                                value={metadataForm.key}
                                onChange={(e) => setMetadataForm({ ...metadataForm, key: e.target.value })}
                                placeholder={t('infoTab.enterKey')}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="metadataValue">{t('infoTab.value')}</Label>
                            <Input
                                type="text"
                                id="metadataValue"
                                value={metadataForm.value}
                                onChange={(e) => setMetadataForm({ ...metadataForm, value: e.target.value })}
                                placeholder={t('infoTab.enterValue')}
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowMetadataModal(false)}>{t('common.cancel')}</Button>
                    <Button color="primary" onClick={handleSaveMetadata} disabled={loading}>
                        {loading ? t('common.saving') : t('common.save')}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
